/* ==========================================================================
   FILE: global/layouts/AuthLayout.jsx
   IAMONEAI - Authentication Layout
   Layout for login, register, verification, and onboarding pages
   ========================================================================== */

import React from 'react';
import { useBreakpoint } from '../hooks';
import './AuthLayout.css';

// ==========================================================================
// AUTH LAYOUT COMPONENT
// ==========================================================================

/**
 * Layout for authentication pages (login, register, verify, etc.)
 * 
 * Desktop: Two columns - branding left, form right
 * Mobile: Single column with logo at top
 * 
 * @param {Object} props
 * @param {React.ReactNode} children - Form content
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle/description
 * @param {'login'|'register'|'verify'|'onboarding'|'waitlist'} variant - Page type
 * @param {boolean} showBranding - Show branding panel (desktop)
 * 
 * @example
 * <AuthLayout
 *   title="Welcome Back"
 *   subtitle="Sign in to your IAMONEAI account"
 *   variant="login"
 * >
 *   <LoginForm />
 * </AuthLayout>
 */
export function AuthLayout({
  children,
  title,
  subtitle,
  variant = 'login',
  showBranding = true,
}) {
  const { isMobile } = useBreakpoint();

  // Branding content based on variant
  const brandingContent = {
    login: {
      tagline: 'Your brain. Your rules. Your guardian.',
      features: [
        { icon: '🧠', text: 'Semantic memory that learns from you' },
        { icon: '🛡️', text: 'Guardrails you control' },
        { icon: '🔗', text: 'Blockchain-verified identity' },
      ],
    },
    register: {
      tagline: 'Create your digital presence',
      features: [
        { icon: '🎯', text: 'AI that remembers what matters to you' },
        { icon: '🔒', text: 'Your data stays yours' },
        { icon: '⚡', text: 'Personalized intelligence' },
      ],
    },
    verify: {
      tagline: 'Almost there!',
      features: [
        { icon: '✉️', text: 'Check your email for the code' },
        { icon: '🔐', text: 'Security is our priority' },
        { icon: '⏱️', text: 'Code expires in 10 minutes' },
      ],
    },
    onboarding: {
      tagline: 'Let\'s personalize your experience',
      features: [
        { icon: '🎨', text: 'Set your communication style' },
        { icon: '🚧', text: 'Define your guardrails' },
        { icon: '🧬', text: 'Shape your AI personality' },
      ],
    },
    waitlist: {
      tagline: 'Join the future of personal AI',
      features: [
        { icon: '🚀', text: 'Be among the first to experience IAMONEAI' },
        { icon: '🎁', text: 'Early adopter benefits' },
        { icon: '📬', text: 'We\'ll notify you when ready' },
      ],
    },
  };

  const branding = brandingContent[variant] || brandingContent.login;

  return (
    <div className={`auth-layout auth-layout-${variant}`}>
      {/* Branding Panel (Desktop) */}
      {!isMobile && showBranding && (
        <aside className="auth-branding">
          <div className="auth-branding-content">
            {/* Logo */}
            <div className="auth-branding-logo">
              <span className="auth-branding-logo-icon">🧠</span>
              <span className="auth-branding-logo-text">IAMONEAI</span>
            </div>

            {/* Tagline */}
            <h1 className="auth-branding-tagline">{branding.tagline}</h1>

            {/* Features */}
            <ul className="auth-branding-features">
              {branding.features.map((feature, index) => (
                <li key={index} className="auth-branding-feature">
                  <span className="auth-branding-feature-icon">{feature.icon}</span>
                  <span className="auth-branding-feature-text">{feature.text}</span>
                </li>
              ))}
            </ul>

            {/* Two Laws */}
            <div className="auth-branding-laws">
              <div className="auth-branding-law">
                <span className="auth-branding-law-number">1</span>
                <span className="auth-branding-law-text">You Are The Owner</span>
              </div>
              <div className="auth-branding-law">
                <span className="auth-branding-law-number">2</span>
                <span className="auth-branding-law-text">I Protect You</span>
              </div>
            </div>

            {/* Background Decoration */}
            <div className="auth-branding-decoration">
              <div className="auth-branding-circle auth-branding-circle-1" />
              <div className="auth-branding-circle auth-branding-circle-2" />
              <div className="auth-branding-circle auth-branding-circle-3" />
            </div>
          </div>
        </aside>
      )}

      {/* Form Panel */}
      <main className="auth-main">
        <div className="auth-main-content">
          {/* Mobile Logo */}
          {isMobile && (
            <div className="auth-mobile-logo">
              <span className="auth-mobile-logo-icon">🧠</span>
              <span className="auth-mobile-logo-text">IAMONEAI</span>
            </div>
          )}

          {/* Header */}
          {(title || subtitle) && (
            <header className="auth-header">
              {title && <h2 className="auth-title">{title}</h2>}
              {subtitle && <p className="auth-subtitle">{subtitle}</p>}
            </header>
          )}

          {/* Form Content */}
          <div className="auth-form-container">
            {children}
          </div>

          {/* Footer */}
          <footer className="auth-footer">
            <p className="auth-footer-text">
              © {new Date().getFullYear()} IAMONEAI. All rights reserved.
            </p>
            <div className="auth-footer-links">
              <a href="/privacy" className="auth-footer-link">Privacy</a>
              <span className="auth-footer-divider">·</span>
              <a href="/terms" className="auth-footer-link">Terms</a>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}

// ==========================================================================
// AUTH FORM CARD
// ==========================================================================

/**
 * Card wrapper for auth form content
 * 
 * @example
 * <AuthFormCard>
 *   <Input label="Email" />
 *   <Button>Continue</Button>
 * </AuthFormCard>
 */
export function AuthFormCard({
  children,
  className = '',
}) {
  return (
    <div className={`auth-form-card ${className}`.trim()}>
      {children}
    </div>
  );
}

// ==========================================================================
// AUTH DIVIDER
// ==========================================================================

/**
 * Divider with optional text (e.g., "or")
 * 
 * @example
 * <AuthDivider text="or continue with" />
 */
export function AuthDivider({
  text,
  className = '',
}) {
  return (
    <div className={`auth-divider ${className}`.trim()}>
      {text ? (
        <>
          <span className="auth-divider-line" />
          <span className="auth-divider-text">{text}</span>
          <span className="auth-divider-line" />
        </>
      ) : (
        <span className="auth-divider-line auth-divider-line-full" />
      )}
    </div>
  );
}

// ==========================================================================
// AUTH SOCIAL BUTTONS
// ==========================================================================

/**
 * Social login buttons (for future OAuth integration)
 * 
 * @example
 * <AuthSocialButtons
 *   onGoogleClick={handleGoogle}
 *   onWalletClick={handleWallet}
 * />
 */
export function AuthSocialButtons({
  onGoogleClick,
  onWalletClick,
  disabled = false,
  className = '',
}) {
  return (
    <div className={`auth-social-buttons ${className}`.trim()}>
      {onGoogleClick && (
        <button
          type="button"
          className="auth-social-btn auth-social-btn-google"
          onClick={onGoogleClick}
          disabled={disabled}
        >
          <svg className="auth-social-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>Continue with Google</span>
        </button>
      )}

      {onWalletClick && (
        <button
          type="button"
          className="auth-social-btn auth-social-btn-wallet"
          onClick={onWalletClick}
          disabled={disabled}
        >
          <svg className="auth-social-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/>
            <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>
          </svg>
          <span>Connect Wallet</span>
        </button>
      )}
    </div>
  );
}

// ==========================================================================
// AUTH LINK
// ==========================================================================

/**
 * Link row for switching between auth pages
 * 
 * @example
 * <AuthLink
 *   text="Don't have an account?"
 *   linkText="Sign up"
 *   href="/register"
 * />
 */
export function AuthLink({
  text,
  linkText,
  href,
  onClick,
  className = '',
}) {
  return (
    <p className={`auth-link-row ${className}`.trim()}>
      {text && <span className="auth-link-text">{text}</span>}
      {onClick ? (
        <button
          type="button"
          className="auth-link"
          onClick={onClick}
        >
          {linkText}
        </button>
      ) : (
        <a href={href} className="auth-link">
          {linkText}
        </a>
      )}
    </p>
  );
}

// ==========================================================================
// AUTH STEPS INDICATOR
// ==========================================================================

/**
 * Step indicator for multi-step flows
 * 
 * @example
 * <AuthSteps
 *   currentStep={2}
 *   totalSteps={4}
 *   labels={['Email', 'Verify', 'Profile', 'Complete']}
 * />
 */
export function AuthSteps({
  currentStep = 1,
  totalSteps = 3,
  labels = [],
  className = '',
}) {
  return (
    <div className={`auth-steps ${className}`.trim()}>
      {/* Progress Bar */}
      <div className="auth-steps-progress">
        <div
          className="auth-steps-progress-fill"
          style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
        />
      </div>

      {/* Step Dots */}
      <div className="auth-steps-dots">
        {Array.from({ length: totalSteps }).map((_, index) => {
          const stepNum = index + 1;
          const isCompleted = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;

          return (
            <div
              key={index}
              className={[
                'auth-step-dot',
                isCompleted && 'auth-step-dot-completed',
                isCurrent && 'auth-step-dot-current',
              ].filter(Boolean).join(' ')}
            >
              {isCompleted ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <span>{stepNum}</span>
              )}
              {labels[index] && (
                <span className="auth-step-label">{labels[index]}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==========================================================================
// AUTH MESSAGE
// ==========================================================================

/**
 * Status message for auth pages (success, error, info)
 * 
 * @example
 * <AuthMessage
 *   type="error"
 *   message="Invalid email or password"
 * />
 */
export function AuthMessage({
  type = 'info', // 'info' | 'success' | 'error' | 'warning'
  message,
  onDismiss,
  className = '',
}) {
  if (!message) return null;

  const icons = {
    info: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    ),
    success: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    error: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="m15 9-6 6" />
        <path d="m9 9 6 6" />
      </svg>
    ),
    warning: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </svg>
    ),
  };

  return (
    <div className={`auth-message auth-message-${type} ${className}`.trim()}>
      <span className="auth-message-icon">{icons[type]}</span>
      <span className="auth-message-text">{message}</span>
      {onDismiss && (
        <button
          type="button"
          className="auth-message-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default AuthLayout;